import { createPortal } from "react-dom";
import { BrandLink } from "../components/BrandLink";
import { MessageUsButton } from "../lib/contact";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, Phone, MessageSquare, Copy, ChevronDown, Calendar, FileText, Languages, Navigation, Check, CloudDownload, CloudUpload, WifiOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/supabase";
import { detectLang, t as T } from "../lib/i18n";
import { useRobotsMeta, usePublicDispatch, useChecklist, useTranslation } from "../hooks/useContractor";
import { MaskedField } from "../components/MaskedField";
import { StaticMap } from "../components/StaticMap";
import { SlideToComplete } from "../components/SlideToComplete";
import { CompletionSheet, ProofCard } from "../components/CompletionProof";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { getState, subscribe, hasSavedLanguage, builtinCode } from "../lib/gtranslate";
import { useOnline } from "../lib/net";
import { enqueueComplete, saveJob, useContractorQueue, wireQueue } from "../lib/contractorOffline";
import { claimSecret, claimState } from "../lib/claim";
import { ClaimCard, TakenBanner, MineBanner, QueueNotice } from "../components/ClaimCard";

const isTouch = () => typeof window !== "undefined" && (navigator.maxTouchPoints > 0 || window.innerWidth < 900);

export const Footer = ({ t }) => (
  <footer className="viral-footer" data-testid="viral-footer">
    <p className="text-sm"><Zap size={14} className="inline text-primary" /> {t.footerLine.split("DropIt").map((part, i, arr) => <span key={i}>{part.trim()}{i < arr.length - 1 && <span className="notranslate" translate="no">{"\u00a0DropIt\u00a0"}</span>}</span>)}</p>
    <Link to="/" className="btn-primary inline-block mt-3 w-auto px-6 notranslate" translate="no" data-testid="footer-try-free">{t.tryFree}</Link>
    <p className="text-xs text-muted mt-4"><Link to="/terms" data-testid="footer-terms">{t.terms}</Link> · <Link to="/privacy" data-testid="footer-privacy">{t.privacy}</Link></p>
  </footer>
);

/* Completed state only: "save your report" — the link + PDF live 14 days after completion (until expires_at). Date stays untranslated. */
const KeepReportNotice = ({ t, expiresAt }) => {
  const until = expiresAt ? t.keepReportUntil.split("{date}") : null;
  return (
    <p className="keep-report mt-5" role="note" data-testid="keep-report-notice">
      {t.keepReport.split("{until}")[0]}{until && <>{until[0]}<span className="notranslate" translate="no" data-testid="keep-report-date">{new Date(expiresAt).toLocaleDateString()}</span>{until[1]}</>}{t.keepReport.split("{until}")[1]}
    </p>
  );
};

export default function ContractorView() {
  const { token } = useParams();
  useRobotsMeta();
  const [gtLang, setGtLang] = useState(() => getState().lang);
  const lang = hasSavedLanguage() ? (builtinCode(gtLang) || "en") : detectLang();
  const t = T(lang);
  const [job, setJob, meta, setMeta] = usePublicDispatch(token);
  const queue = useContractorQueue();
  const online = useOnline();
  const [secret, setSecret] = useState(null);
  const [justTaken, setJustTaken] = useState(false);
  const noticeKey = `dropit-notice-${token}`;
  const [notice, setNotice] = useState(() => { try { return JSON.parse(localStorage.getItem(noticeKey)); } catch { return null; } });
  const showNotice = (n) => { setNotice(n); localStorage.setItem(noticeKey, JSON.stringify(n)); };
  const dismissNotice = () => { setNotice(null); localStorage.removeItem(noticeKey); };
  useEffect(() => { claimSecret(token).then(setSecret).catch(() => setSecret({ hash: null })); }, [token]);
  const unclaimedOpen = !!job && "claimed_at" in job && !job.claimed_at && job.status !== "completed";
  useEffect(() => {  // keep the page truthful while open/backgrounded: single refetch on focus/visibility/online; light poll only while unclaimed + visible
    const kick = () => { if (document.visibilityState === "visible" && navigator.onLine) reload(); };
    document.addEventListener("visibilitychange", kick); window.addEventListener("focus", kick); window.addEventListener("online", kick);
    const poll = unclaimedOpen ? setInterval(kick, 20000) : null;
    return () => { document.removeEventListener("visibilitychange", kick); window.removeEventListener("focus", kick); window.removeEventListener("online", kick); if (poll) clearInterval(poll); };
  }, [unclaimedOpen, token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    wireQueue((q, status, body) => {
      if (q.token !== token) return;
      if (status === 403 && body?.error === "not_claimer") showNotice({ kind: body.status === "completed" ? "completed_by" : "taken_by", name: body.claimed_by_name });
      else if (status === 403 && body?.error === "not_claimed") showNotice({ kind: "not_claimed" });
      else if (status === 409 && body?.error === "already_claimed") showNotice({ kind: "taken_by", name: body.claimed_by_name });
      else if (status === 409 && q.type === "completion") showNotice({ kind: "proof_locked" });
      if (status < 500) reload();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [checks, toggle] = useChecklist(token, job?.tasks?.length || 0);
  const tr = useTranslation(job, hasSavedLanguage() ? gtLang : lang);
  const [showNotes, setShowNotes] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [proofSheet, setProofSheet] = useState(false);
  const header = (
    <header className="topbar">
      <BrandLink to="/" testid="contractor-brand-link" />
      <div className="flex items-center gap-2">
        <LanguageSwitcher />
      </div>
    </header>
  );
  const reload = async () => { try { const rec = await claimSecret(token).catch(() => null); const r = await fetch(`${API}/public/dispatch/${token}`, { cache: "no-store", headers: { "X-Claim-Hash": rec?.hash || "" } }); if (r.ok && r.headers.get("x-sw-offline") !== "1") { const d = await r.json(); setJob(d); saveJob(token, d); setMeta({ source: "network", saved: true, offlineNoCache: false, expired: false }); } } catch { /* keep current */ } };
  useEffect(() => subscribe((s) => setGtLang(s.lang)), []);


  if (job === undefined) return <main className="shell" data-testid="contractor-page">{header}<p className="text-muted" data-testid="contractor-loading">{t.loading}</p></main>;
  if (job === null && meta.offlineNoCache) return (
    <main className="shell" data-testid="contractor-page">{header}
      <div className="card text-center py-10" data-testid="contractor-needs-internet"><WifiOff className="mx-auto mb-3 text-muted" size={28} /><p className="font-bold text-lg">{t.needsInternetTitle}</p><p className="text-sm text-muted mt-2">{t.needsInternetBody}</p><p className="text-sm text-muted mt-4 flex items-center justify-center gap-2">Need help? <MessageUsButton className="btn-secondary !w-auto !py-1.5 !px-3 text-sm" testid="contractor-offline-message-us" /></p></div>
      <Footer t={t} />
    </main>
  );
  if (job === null) return (
    <main className="shell" data-testid="contractor-page">{header}
      <div className="card text-center py-10" data-testid="contractor-not-found"><p className="font-bold text-lg">{t.expiredTitle}</p><p className="text-sm text-muted mt-2">{t.expiredBody}</p><p className="text-sm text-muted mt-4 flex items-center justify-center gap-2">Need help? <MessageUsButton className="btn-secondary !w-auto !py-1.5 !px-3 text-sm" testid="contractor-message-us" /></p></div>
      <Footer t={t} />
    </main>
  );

  const view = tr.show && tr.data ? { ...job, ...tr.data } : job;
  const done = job.status === "completed";
  const claim = claimState(job, secret);
  const canAct = claim === "legacy" || claim === "mine" || (claim === "completed" && (job.claim_mine || secret?.claimed_at));
  const hash = secret?.hash || null;
  const statusCls = done ? "banner-done" : job.status === "viewed" ? "banner-progress" : "banner-pending";
  const statusText = done ? <>{t.completedAt} <span className="notranslate" translate="no">{new Date(job.completed_at).toLocaleString()}</span>{job.completed_by_name && <span className="block text-xs font-normal mt-1" data-testid="completed-by">{t.completedBy.split("{name}")[0]}<span className="notranslate" translate="no">{job.completed_by_name}</span>{t.completedBy.split("{name}")[1]}</span>}</> : job.status === "viewed" ? t.inProgress : t.pending;
  const { latitude: lat, longitude: lng } = job;
  const phone = job.sender_phone;

  const complete = async () => {
    setCompleting(true);
    const now = new Date().toISOString();
    let r = null;
    try { r = await fetch(`${API}/public/dispatch/${token}/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_completed_at: now, secret_hash: hash }) }); }
    catch { r = null; }
    setCompleting(false);
    if (r?.ok) { const b = await r.json().catch(() => ({})); setJob({ ...job, status: "completed", completed_at: b.completed_at || now, completed_at_client: b.completed_at_client || now, completed_by_name: b.completed_by_name || job.completed_by_name }); setProofSheet(true); }
    else if (!r) { await enqueueComplete(token, now, hash); const next = { ...job, status: "completed", completed_at: now, completed_at_client: now, queued: true }; setJob(next); saveJob(token, { ...job }); toast.message(t.willSync); setProofSheet(true); }
    else if (r.status === 403) { const b = await r.json().catch(() => ({})); showNotice(b.error === "not_claimed" ? { kind: "not_claimed" } : { kind: b.status === "completed" ? "completed_by" : "taken_by", name: b.claimed_by_name }); reload(); }
    else toast.error("Could not complete this job.");
  };

  const copyPhone = async () => { try { await navigator.clipboard.writeText(phone); toast.success(t.copied); } catch { window.prompt(t.copy, phone); } };

  return (
    <main className="shell" data-testid="contractor-page">
      {header}
      {(queue.pending > 0 || meta.source === "cache" || meta.saved) && (
        <p className={`offline-badge ${queue.pending > 0 ? "is-queued" : meta.source === "cache" ? "is-offline" : "is-saved"}`} data-testid="offline-badge" data-state={queue.pending > 0 ? "queued" : meta.source === "cache" ? "offline" : "saved"}>
          {queue.pending > 0 ? <><CloudUpload size={12} /> {t.willSync}</> : meta.source === "cache" ? <><WifiOff size={12} /> {t.offlineSaved}</> : <><CloudDownload size={12} /> {t.savedOffline}</>}
        </p>
      )}
      {meta.expired && <p className="offline-badge is-offline" data-testid="link-expired-readonly"><Lock size={12} /> {t.linkExpiredReadOnly}</p>}
      <section className="card fade-up" data-testid="contractor-job">
        <div className="flex items-start justify-between gap-3">
          <p className="eyebrow" data-testid="contractor-sender">⚡ {t.dispatchedBy} <span className="notranslate" translate="no">{job.sender_name || "DropIt"}</span></p>
        </div>
        {phone && (
          <div className="flex gap-2 mt-3" data-testid="contact-row">
            {isTouch() ? (<>
              <a href={`tel:${phone}`} className="send-btn flex-1" data-testid="call-sender"><Phone size={18} /> {t.call}</a>
              <a href={`sms:${phone}`} className="send-btn flex-1" data-testid="text-sender"><MessageSquare size={18} /> {t.text}</a>
            </>) : (
              <button type="button" className="send-btn flex-1" onClick={copyPhone} data-testid="copy-sender-phone"><Phone size={18} /> {phone} <Copy size={14} /></button>
            )}
          </div>
        )}
        <div className={`status-banner mt-4 ${statusCls}`} data-testid="status-banner" data-status={job.status}>{statusText}</div>
        <QueueNotice notice={notice} t={t} onDismiss={dismissNotice} />
        {claim === "taken" && <TakenBanner job={job} t={t} justNow={justTaken} />}
        {claim === "mine" && <MineBanner job={job} t={t} />}
        <h2 className="job-title mt-4" data-testid="contractor-title">{view.title || view.task_summary}</h2>
        {job.site_label && <p className="text-sm text-muted mt-1" data-testid="contractor-site">{job.site_label}</p>}

        {view.hazards?.length > 0 ? (
          <div className="hazard-box mt-4" data-testid="contractor-hazards">
            <p className="eyebrow text-[#1a1500]">⚠ {t.hazards}</p>
            <div className="flex flex-wrap gap-2 mt-2">{view.hazards.map((h, i) => <span key={i} className="chip chip-warn">{h}</span>)}</div>
            <p className="text-xs mt-2 text-[#5a4d00]" data-testid="hazard-disclaimer">⚠︎ {t.hazardDisclaimer}</p>
          </div>
        ) : (
          <div className="mt-4" data-testid="contractor-hazards-empty">
            <p className="eyebrow">⚠ {t.hazards}</p>
            <p className="text-xs text-muted mt-1" data-testid="hazard-disclaimer">⚠︎ {t.hazardDisclaimer}</p>
          </div>
        )}

        <p className="eyebrow mt-5">{t.tasks}</p>
        <ul className="mt-2 space-y-2" data-testid="contractor-tasks">
          {(view.tasks || []).map((task, i) => (
            <li key={i}>
              <button type="button" className={`task-check ${checks[i] ? "checked" : ""}`} onClick={() => toggle(i)} data-testid={`task-${i}`} aria-pressed={!!checks[i]}>
                <span className="task-box">{checks[i] && <Check size={14} strokeWidth={3} />}</span><span>{task}</span>
              </button>
            </li>
          ))}
        </ul>

        {tr.needs && (
          <button type="button" className="btn-secondary mt-4 flex items-center justify-center gap-2" onClick={tr.run} disabled={tr.busy} data-testid="translate-button">
            <Languages size={16} /> {tr.busy ? t.translating : tr.show ? t.showOriginal : t.translate}
          </button>
        )}

        {view.transcript && (
          <div className="mt-4">
            <button type="button" className="flex items-center gap-1 text-sm text-muted" onClick={() => setShowNotes(!showNotes)} data-testid="notes-toggle">
              <ChevronDown size={14} className={showNotes ? "rotate-180" : ""} /> {showNotes ? t.hideNotes : t.showNotes}
            </button>
            {showNotes && <p className="result text-sm text-muted mt-2" data-testid="contractor-notes">“{view.transcript}”</p>}
          </div>
        )}

        {job.photo_signed_urls?.length > 0 && (
          <div className="mt-5" data-testid="contractor-photos">
            <p className="eyebrow">{t.photos}</p>
            <div className="photo-grid mt-2" data-testid="job-photos">{job.photo_signed_urls.map((u, i) => <button type="button" key={i} className="photo-tile" onClick={() => setLightbox(u)} data-testid={`photo-${i}`}><img src={u} alt="" loading="lazy" /></button>)}</div>
          </div>
        )}
        {job.audio_url && (
          <div className="mt-5" data-testid="contractor-audio"><p className="eyebrow">{t.audioNote}</p><audio controls src={job.audio_url} className="w-full mt-2" /></div>
        )}
      </section>

      {(claim === "unclaimed" || claim === "released") && !meta.expired && secret && (
        <ClaimCard token={token} job={job} t={t} hash={hash} online={online} released={claim === "released"}
          onClaimed={(b) => { setJustTaken(false); dismissNotice(); const next = { ...job, claimed_at: b.claimed_at, claimed_by_name: b.claimed_by_name, claim_mine: true, claim_released_at: null }; setJob(next); saveJob(token, next); reload(); }}
          onTaken={(b) => { setJustTaken(true); setJob({ ...job, claimed_at: b.claimed_at, claimed_by_name: b.claimed_by_name, claim_mine: false }); }} />
      )}

      {(job.access_code || job.access_notes) && (
        <section className="card mt-5 fade-up" data-testid="access-section">
          <p className="eyebrow mb-2">{t.access}</p>
          <MaskedField label={t.gateCode} value={job.access_code} revealLabel={t.tapToReveal} hideLabel={t.hide} testid="gate-code" />
          <MaskedField label={t.accessNotes} value={job.access_notes} revealLabel={t.tapToReveal} hideLabel={t.hide} testid="access-notes" />
        </section>
      )}

      {lat != null && (
        <section className="card mt-5 p-0 overflow-hidden fade-up" data-testid="nav-section">
          <StaticMap lat={lat} lng={lng} nav={false} t={t} />
          <div className="p-4">
            <p className="eyebrow mb-2"><Navigation size={12} className="inline" /> {t.navigate}</p>
            <div className="grid grid-cols-3 gap-2">
              <a className="send-btn text-sm" href={`https://maps.apple.com/?daddr=${lat},${lng}`} target="_blank" rel="noreferrer" data-testid="nav-apple" translate="no"><span className="notranslate">{t.appleMaps}</span></a>
              <a className="send-btn text-sm" href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`} target="_blank" rel="noreferrer" data-testid="nav-google" translate="no"><span className="notranslate">{t.googleMaps}</span></a>
              <a className="send-btn text-sm" href={`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`} target="_blank" rel="noreferrer" data-testid="nav-waze" translate="no"><span className="notranslate">{t.waze}</span></a>
            </div>
          </div>
        </section>
      )}

      {done && !meta.expired && <KeepReportNotice t={t} expiresAt={job.expires_at} />}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <a className="send-btn" href={`${API}/public/dispatch/${token}/calendar.ics`} data-testid="export-ics"><Calendar size={18} /> {t.addToCalendar}</a>
        <a className="send-btn" href={`${API}/public/dispatch/${token}/report.pdf`} target="_blank" rel="noopener noreferrer" data-testid="export-pdf"><FileText size={18} /> {t.councilPdf}</a>
      </div>


      <ProofCard token={token} job={job} t={t} onUpdate={setJob} onOpen={setLightbox} onLocked={reload} secretHash={hash} canEdit={canAct} />

      {canAct && <div className="mt-6">
        <SlideToComplete label={t.slideToComplete} releaseLabel={t.release} busyLabel={t.marking} doneLabel={t.done} onComplete={complete} done={done} busy={completing} disabled={job.status === "canceled" || job.status === "draft"} />
      </div>}

      <Footer t={t} />
      {proofSheet && canAct && <CompletionSheet token={token} job={job} t={t} secretHash={hash} onDone={(d) => { if (d) setJob(d); else reload(); setProofSheet(false); }} onSkip={() => setProofSheet(false)} />}
      {lightbox && createPortal(<div className="lightbox" onClick={() => setLightbox(null)} data-testid="lightbox"><img src={lightbox} alt="" /></div>, document.body)}
    </main>
  );
}
